<template>
  <div class="rds-picker">
    <!-- Breadcrumb trail -->
    <div class="rds-crumbs">
      <span v-for="(crumb, i) in breadcrumbs" :key="crumb.id">
        <a class="rds-crumb" @click="crumbTo(crumb, i)">{{ crumb.name }}</a>
        <span v-if="i + 1 < breadcrumbs.length" class="rds-sep"> / </span>
      </span>
    </div>

    <p v-if="loading" class="rds-muted">
      <i class="fas fa-spinner fa-spin"></i> Loading…
    </p>
    <div v-else-if="error" class="rds-error">{{ error }}</div>
    <div v-else class="rds-list">
      <div
        v-for="folder in folders"
        :key="folder.id"
        class="rds-row"
        @click="browseFolder(folder)"
      >
        <i class="fas fa-folder rds-icon"></i>
        <span class="rds-name">{{ folder.name }}</span>
      </div>
      <div
        v-for="dataset in datasets"
        :key="dataset.id"
        class="rds-row rds-dataset"
        @click="$emit('select', dataset)"
      >
        <i
          class="fas rds-icon"
          :class="mode === 'raster' ? 'fa-border-none' : 'fa-map-marker-alt'"
        ></i>
        <span class="rds-name">{{ dataset.name }}</span>
      </div>
      <p v-if="!folders.length && !datasets.length" class="rds-muted">
        No {{ mode }} datasets in this folder.
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
const fb = require('@/firebaseConfig.js')

export default {
  name: 'RasterDatasetPicker',
  props: {
    // 'raster' shows only raster datasets; 'point' shows only point datasets.
    mode: {
      type: String,
      required: true
    },
    farmId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      breadcrumbs: [{ id: 'root', name: 'Root' }],
      folders: [],
      datasets: [],
      loading: false,
      error: null
    }
  },
  mounted () {
    this.queryFolder('root')
  },
  methods: {
    /**
     * Load one folder's child folders + datasets straight from Firestore,
     * replacing the legacy api.efficientvineyard.com/datasets|folders
     * calls. Datasets are filtered to the picker's mode.
     */
    queryFolder (folderId) {
      this.loading = true
      this.error = null
      Promise.all([
        fb.foldersCollection
          .where('farmId', '==', this.farmId)
          .where('folderId', '==', folderId)
          .orderBy('name', 'asc').get(),
        fb.datasetsCollection
          .where('farmId', '==', this.farmId)
          .where('folderId', '==', folderId)
          .orderBy('updatedOn', 'desc').get()
      ])
        .then(([folderSnap, datasetSnap]) => {
          this.folders = folderSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          this.datasets = datasetSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => this.matchesMode(d))
        })
        .catch(err => {
          this.error = 'Could not load datasets: ' + (err.message || err)
        })
        .finally(() => { this.loading = false })
    },
    matchesMode (dataset) {
      const file = dataset.file
      if (!file) return false
      if (this.mode === 'raster') return !!file.raster
      // point mode — a vector dataset with a GeoJSON, not a raster
      return !!file.geoJsonPath && !file.raster
    },
    browseFolder (folder) {
      this.breadcrumbs.push({ id: folder.id, name: folder.name })
      this.queryFolder(folder.id)
    },
    crumbTo (crumb, index) {
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1)
      this.queryFolder(crumb.id)
    }
  }
}
</script>

<style scoped>
.rds-picker {
  border: 1px solid #d6dbe0;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fff;
}
.rds-crumbs {
  font-size: 0.78rem;
  margin-bottom: 6px;
}
.rds-crumb { color: #066739; cursor: pointer; }
.rds-crumb:hover { text-decoration: underline; }
.rds-sep { color: #b0b7bd; }
.rds-list {
  max-height: 220px;
  overflow-y: auto;
}
.rds-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.86rem;
  color: #2c3e50;
}
.rds-row:hover { background: #f2f5f3; }
.rds-icon { color: #95a5a6; width: 16px; text-align: center; }
.rds-name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rds-muted { color: #889; font-size: 0.82rem; margin: 4px 0 0; }
.rds-error { color: #c0392b; font-size: 0.82rem; }
</style>
